// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useCallback, useEffect, useState } from 'react';
import { Button, IconButton, AppIcon, Spinner, Alert } from '@loa/ui-components';
import { formatDate } from '@loa/theme';
import { noteApi } from '../../api/noteApi';
import type { Note } from '../../types/note';
import { NoteFormModal } from './notes/NoteFormModal';
import { DeleteNoteModal } from './notes/DeleteNoteModal';
import './RelationshipNotesTab.css';

type RelationshipNotesTabProps = Readonly<{
  profileId: string;
}>;

export function RelationshipNotesTab({ profileId }: RelationshipNotesTabProps) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingNote, setDeletingNote] = useState<Note | null>(null);

  const loadNotes = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await noteApi.getRelationshipNotes(profileId);
      setNotes(data);
    } catch (err) {
      console.error('Failed to load notes:', err);
      setError((err as Error).message || 'Failed to load notes');
    } finally {
      setIsLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const handleNewNote = () => {
    setEditingNote(null);
    setFormOpen(true);
  };

  const handleEditNote = (note: Note) => {
    setEditingNote(note);
    setFormOpen(true);
  };

  const handleDeleteNote = (note: Note) => {
    setDeletingNote(note);
    setDeleteOpen(true);
  };

  const handleFormSaved = async () => {
    setFormOpen(false);
    setEditingNote(null);
    await loadNotes();
  };

  const handleDeleteSuccess = async () => {
    setDeleteOpen(false);
    setDeletingNote(null);
    await loadNotes();
  };

  return (
    <div className="notes-tab">
      <div className="notes-tab-header">
        <h3 className="notes-tab-title">Notes</h3>
        <Button variant="filled" size="lg" onClick={handleNewNote}>
          New Note
        </Button>
      </div>

      <div className="notes-tab-body">
        {isLoading && (
          <div className="notes-tab-loading">
            <Spinner />
          </div>
        )}

        {!isLoading && error && (
          <Alert variant="error" header="Error Loading Notes">
            <p>{error}</p>
          </Alert>
        )}

        {!isLoading && !error && notes.length === 0 && (
          <div className="notes-tab-empty">
            <span className="notes-tab-empty-text">
              No notes yet. Click &ldquo;New Note&rdquo; to add one.
            </span>
          </div>
        )}

        {!isLoading && !error && notes.length > 0 && (
          <ul className="notes-list">
            {notes.map((note) => (
              <li
                key={note.id}
                className="notes-list-item"
                data-testid={`note-card-${note.id}`}
              >
                <h4 className="notes-list-item-title">{note.title}</h4>
                <span className="notes-list-item-date">
                  {formatDate(note.createdAt)}
                </span>
                <div
                  className="notes-list-item-content"
                  dangerouslySetInnerHTML={{ __html: note.content }}
                />
                <div className="notes-list-item-actions">
                  <IconButton
                    variant="round-filled"
                    size="md"
                    onClick={() => handleEditNote(note)}
                    icon={<AppIcon name="edit" size={24} />}
                    aria-label={`Edit note ${note.title}`}
                  />
                  <IconButton
                    variant="round-filled"
                    size="md"
                    onClick={() => handleDeleteNote(note)}
                    icon={<AppIcon name="trash" size={24} />}
                    aria-label={`Delete note ${note.title}`}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {formOpen && (
        <NoteFormModal
          key={editingNote?.id ?? 'new'}
          open
          profileId={profileId}
          note={editingNote}
          onClose={() => {
            setFormOpen(false);
            setEditingNote(null);
          }}
          onSaved={handleFormSaved}
        />
      )}

      {deletingNote && (
        <DeleteNoteModal
          open={deleteOpen}
          profileId={profileId}
          noteId={deletingNote.id}
          onClose={() => {
            setDeleteOpen(false);
            setDeletingNote(null);
          }}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </div>
  );
}
